import { useMutation, useQueryClient } from '@tanstack/react-query';

import { patchSubscription, postResendTc, PREPAID_PRODUCTS_QUERY_KEY } from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

export const useSubscriptionActions = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries([PREPAID_PRODUCTS_QUERY_KEY]);

  const showError = (error: JsonDataItem) => {
    Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
  };

  const { mutate: resendTcMutation, isLoading: isResending } = useMutation(postResendTc, {
    onSuccess: (response) => {
      if (response?.tc_sent) {
        Toast.show({ description: 'Terms and Conditions has been sent.', type: Toast.Type.Success });
      }
      invalidate();
    },
    onError: showError,
  });

  const { mutate: patchMutation, isLoading: isPatching } = useMutation(patchSubscription, {
    onError: showError,
  });

  const resendTc = (id: number) => resendTcMutation(id);

  const updateEmailInvoice = (id: number, email_invoice: 0 | 1, onDone?: () => void) =>
    patchMutation(
      { id, body: { email_invoice } },
      {
        onSuccess: () => {
          Toast.show({ description: 'Email invoice preference updated.', type: Toast.Type.Success });
          invalidate();
          onDone?.();
        },
      },
    );

  const updateComments = (id: number, comments: string, onDone?: () => void) =>
    patchMutation(
      { id, body: { comments } },
      {
        onSuccess: () => {
          Toast.show({ description: 'Comments updated.', type: Toast.Type.Success });
          invalidate();
          onDone?.();
        },
      },
    );

  const cancelSubscription = (id: number, close_reason: string, end_date: string, onDone?: () => void) =>
    patchMutation(
      { id, body: { status: '0', close_reason, end_date } },
      {
        onSuccess: () => {
          Toast.show({ description: 'Subscription cancelled.', type: Toast.Type.Success });
          invalidate();
          onDone?.();
        },
      },
    );

  return {
    resendTc,
    updateEmailInvoice,
    updateComments,
    cancelSubscription,
    isLoading: isResending || isPatching,
  };
};
